import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, 
  Check,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Loader2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ExternalLink,
  FileText,
  Download,
  RefreshCw,
  ShieldAlert,
  Calendar,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { api, useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  PACKAGES,
  formatPackageName,
  getCurrentPackageId
} from '../../data/seoPackages';

export default function BillingPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [dbPrices, setDbPrices] = useState({});
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [switchingPlan, setSwitchingPlan] = useState(null);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchSubscription();
    fetchInvoices();
    api.get('/packages/prices').then((r) => setDbPrices(r.data?.prices || {})).catch(() => {});
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/billing/subscription');
      setSubscription(response.data);
      // If the backend resolved a package that the auth context doesn't know about yet
      // (e.g. admin-assigned on the project, back-filled here), refresh user so
      // ProtectedRoute unlocks navigation without a full page reload.
      const resolved = response.data?.package_type;
      if (resolved && resolved !== user?.package_type) {
        refreshUser();
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await api.get('/billing/invoices');
      setInvoices(response.data.invoices || []);
    } catch {
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await api.get(`/billing/checkout-status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setPaymentStatus('success');
          setCheckingPayment(false);
          toast.success('Payment successful! Your plan is now active.');
          await fetchSubscription();
          await fetchInvoices();
          await refreshUser();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus('pending');
          setCheckingPayment(false);
        }
      } catch {
        setPaymentStatus('error');
        setCheckingPayment(false);
      }
    };
    poll();
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      const response = await api.post('/billing/create-checkout', { package: planId });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchPlan = async (planId) => {
    setSwitchingPlan(planId);
    try {
      const response = await api.post('/billing/switch-plan', { new_package: planId });
      toast.success(response.data.message || 'Plan switched successfully! Proration applied.');
      setShowSwitchConfirm(null);
      await fetchSubscription();
      fetchInvoices();
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch plan');
    } finally {
      setSwitchingPlan(null);
    }
  };

  const handlePlanAction = (planId) => {
    const hasActiveSubscription = subscription?.has_subscription && subscription?.stripe_subscription_id;
    if (hasActiveSubscription && currentPackageId !== planId) {
      setShowSwitchConfirm(planId);
    } else {
      handleSubscribe(planId);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post('/billing/cancel');
      toast.success('Subscription will cancel at end of billing period');
      setShowCancelConfirm(false);
      fetchSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api.post('/billing/reactivate');
      toast.success('Subscription reactivated!');
      fetchSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setReactivating(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const response = await api.post('/billing/portal');
      window.location.href = response.data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const isSuspended = user?.subscription_status === 'suspended';
  const isGracePeriod = subscription?.status === 'grace_period';
  const isCancelPending = subscription?.cancel_at_period_end;
  const hasPendingDowngrade = !!subscription?.pending_downgrade_package;
  // Derive current package with user object as fallback for admin-assigned / legacy subscriptions
  const currentPackageId =
    getCurrentPackageId(subscription) ||
    getCurrentPackageId({ package_type: user?.package_type, plan: user?.plan });

  // Merge admin-editable DB prices over the static defaults
  const packages = PACKAGES.map((p) =>
    dbPrices[p.id] !== undefined ? { ...p, price: dbPrices[p.id] } : p,
  );

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* Welcome banner — shown when arriving from onboarding or signup without a plan */}
      {(searchParams.get('from') === 'onboarding' || searchParams.get('from') === 'signup') && !user?.package_type && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-xl border-2 border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/20 flex items-start gap-4"
        >
          <Sparkles className="w-6 h-6 text-indigo-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-indigo-800 dark:text-indigo-200">
              Welcome to GDS! Pick a plan to activate your account.
            </p>
            <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-0.5">
              If a plan was already promised to you, please{' '}
              <a
                href="mailto:support@godigitalstep.com"
                className="underline underline-offset-2 hover:text-indigo-800 dark:hover:text-indigo-200 transition-colors"
              >
                contact your admin
              </a>
              {' '}and they'll get you set up.
            </p>
          </div>
        </motion.div>
      )}

      {/* Suspended Banner */}
      {isSuspended && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800"
        >
          <div className="flex items-start gap-4">
            <ShieldAlert className="w-8 h-8 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-heading font-bold text-red-800 dark:text-red-200 text-lg">
                Account Suspended
              </h3>
              <p className="text-red-600 dark:text-red-400 mt-1">
                Your account has been suspended due to non-payment. Dashboard access is restricted.
                Please update your payment method to restore access.
              </p>
              <Button className="mt-4 bg-red-600 hover:bg-red-700 text-white" onClick={handleManageBilling} disabled={portalLoading}>
                {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update Payment Method
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Grace Period Warning */}
      {isGracePeriod && !isSuspended && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 flex items-start gap-4"
        >
          <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-800 dark:text-amber-200">Payment Failed - Grace Period</p>
            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
              Your last payment failed. You have until{' '}
              <strong>
                {subscription?.grace_period_end
                  ? format(new Date(subscription.grace_period_end), 'MMM d, yyyy')
                  : '7 days'}
              </strong>{' '}
              to update your payment method before your account is suspended.
            </p>
            <Button variant="outline" size="sm" className="mt-2 border-amber-400 text-amber-700" onClick={handleManageBilling}>
              Update Payment Method
            </Button>
          </div>
        </motion.div>
      )}

      {/* Payment Success Message */}
      {paymentStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center gap-4"
        >
          <CheckCircle2 className="w-6 h-6 text-emerald-500" />
          <div>
            <p className="font-medium text-emerald-800 dark:text-emerald-200">Payment Successful!</p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">Your subscription is now active.</p>
          </div>
        </motion.div>
      )}

      {checkingPayment && (
        <div className="p-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 flex items-center gap-4">
          <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          <div>
            <p className="font-medium text-indigo-800 dark:text-indigo-200">Processing payment...</p>
            <p className="text-sm text-indigo-600 dark:text-indigo-400">Please wait while we confirm your payment.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Billing & Plans</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage your subscription and billing
          </p>
        </div>
        {subscription?.has_subscription && subscription?.stripe_subscription_id && (
          <Button variant="outline" onClick={handleManageBilling} disabled={portalLoading}>
            {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
            Manage Billing
          </Button>
        )}
      </div>

      {/* Current Plan */}
      {subscription?.has_subscription && (
        <Card className={`gds-card border-2 ${
          isCancelPending ? 'border-amber-400' : isSuspended ? 'border-red-400' : 'border-indigo-500'
        }`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isSuspended ? 'bg-red-100 dark:bg-red-900/30' : 'bg-indigo-100 dark:bg-indigo-900/30'
                }`}>
                  <CreditCard className={`w-6 h-6 ${isSuspended ? 'text-red-600' : 'text-indigo-600'}`} />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Current Plan</p>
                  <p className="font-heading font-bold text-xl">
                    {formatPackageName(currentPackageId) || 'Plan syncing...'}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {subscription.status === 'active' && !isCancelPending && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Active
                      </span>
                    )}
                    {subscription.status === 'active' && isCancelPending && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" /> Cancels at period end
                      </span>
                    )}
                    {subscription.status === 'grace_period' && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" /> Payment overdue
                      </span>
                    )}
                    {subscription.status === 'suspended' && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                        <XCircle className="w-3 h-3" /> Suspended
                      </span>
                    )}
                    {subscription.status === 'cancelled' && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">
                        <XCircle className="w-3 h-3" /> Cancelled
                      </span>
                    )}
                    {hasPendingDowngrade && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                        <AlertCircle className="w-3 h-3" /> Downgrade pending
                      </span>
                    )}
                  </div>
                  {hasPendingDowngrade && (
                    <p className="text-xs text-orange-600 mt-1">
                      Downgrading to <span className="font-semibold capitalize">{subscription.pending_downgrade_package}</span>
                      {subscription.pending_downgrade_effective_at
                        ? ` on ${formatDistanceToNow(new Date(subscription.pending_downgrade_effective_at), { addSuffix: true })}`
                        : ' at next renewal'}.
                    </p>
                  )}
                </div>
              </div>
              <div className="text-right">
                {subscription.amount ? (
                  <>
                    <p className="text-2xl font-bold font-heading">${subscription.amount?.toLocaleString()}</p>
                    <p className="text-sm text-slate-500">/month</p>
                  </>
                ) : null}
                {subscription.current_period_end && (
                  <p className="text-xs text-slate-400 flex items-center justify-end gap-1 mt-1">
                    <Calendar className="w-3 h-3" />
                    {isCancelPending ? 'Ends' : 'Renews'}{' '}
                    {formatDistanceToNow(new Date(subscription.current_period_end), { addSuffix: true })}
                  </p>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
              {isCancelPending ? (
                <Button variant="outline" size="sm" onClick={handleReactivate} disabled={reactivating}>
                  {reactivating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Reactivate Subscription
                </Button>
              ) : subscription.status === 'active' ? (
                <>
                  {!showCancelConfirm ? (
                    <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-500" onClick={() => setShowCancelConfirm(true)}>
                      Cancel Subscription
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-red-600">Are you sure?</span>
                      <Button variant="destructive" size="sm" onClick={handleCancel} disabled={cancelling}>
                        {cancelling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Yes, Cancel
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setShowCancelConfirm(false)}>
                        No, Keep
                      </Button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid lg:grid-cols-4 gap-6">
        {packages.map((plan, i) => {
          const isCurrentPlan = currentPackageId === plan.id;
          const hasCurrentPackage = !!currentPackageId;
          // Once a package is resolved, every other package is a plan change target.
          // Users without a Stripe subscription still go through checkout when clicked.
          const isSwitchTarget = hasCurrentPackage && !isCurrentPlan;
          const currentPrice = packages.find((p) => p.id === currentPackageId)?.price || 0;
          const isUpgrade = isSwitchTarget && plan.price > currentPrice;
          const isDowngrade = isSwitchTarget && plan.price < currentPrice;
          const scheduledDowngradeTarget = (subscription?.pending_downgrade_package || '').toLowerCase();
          const isScheduledDowngradeTarget = !!scheduledDowngradeTarget && scheduledDowngradeTarget === plan.id;
          const isDowngradeLockedByPendingChange = hasPendingDowngrade && isDowngrade && !isScheduledDowngradeTarget;
          const isActionDisabled =
            loading ||
            isCurrentPlan ||
            !!switchingPlan ||
            isScheduledDowngradeTarget ||
            isDowngradeLockedByPendingChange;

          // Button style: upgrade=primary, downgrade=outline, current=ghost disabled, default=primary
          const buttonClass = isCurrentPlan
            ? 'w-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 cursor-default'
            : isDowngrade || isDowngradeLockedByPendingChange || isScheduledDowngradeTarget
            ? 'w-full gds-btn-secondary'
            : 'w-full gds-btn-primary';

          return (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
            >
              <Card className={`h-full relative overflow-hidden transition-all duration-300 ${
                isCurrentPlan
                  ? 'border-2 border-emerald-500 shadow-lg shadow-emerald-500/10 dark:shadow-emerald-500/5'
                  : plan.popular
                  ? 'border-2 border-indigo-500 shadow-xl shadow-indigo-500/10'
                  : 'border border-slate-200 dark:border-slate-800'
              }`}>
                {/* Top-right badge: Most Popular / Enterprise (only when not current plan) */}
                {plan.badge && !isCurrentPlan && (
                  <div className={`absolute top-0 right-0 px-4 py-1 text-xs font-medium ${
                    plan.popular
                      ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}>
                    {plan.badge}
                  </div>
                )}
                {/* Active plan badge */}
                {isCurrentPlan && (
                  <div className="absolute top-0 left-0 right-0 px-4 py-1.5 text-xs font-semibold bg-emerald-500 text-white flex items-center justify-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" />
                    Your Current Plan
                  </div>
                )}
                <CardContent className={`p-8 flex flex-col h-full ${isCurrentPlan ? 'pt-10' : ''}`}>
                  <h3 className="font-heading font-bold text-xl mb-2">{plan.name}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">{plan.description}</p>
                  
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-heading text-4xl font-bold">${plan.price.toLocaleString()}</span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>

                  {/* Price delta hint for upgrade/downgrade */}
                  {currentPrice > 0 && !isCurrentPlan && (
                    <p className={`text-xs mb-5 font-medium ${isUpgrade ? 'text-indigo-500' : 'text-slate-400'}`}>
                      {isUpgrade
                        ? `+$${(plan.price - currentPrice).toLocaleString()}/mo more`
                        : isDowngrade
                        ? `-$${(currentPrice - plan.price).toLocaleString()}/mo less`
                        : ''}
                    </p>
                  )}
                  {!(currentPrice > 0 && !isCurrentPlan) && <div className="mb-5" />}
                  
                  <div className="space-y-3 mb-6 flex-1">
                    {plan.highlights.map((feature, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  {showSwitchConfirm === plan.id ? (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600 dark:text-slate-400 text-center">
                        {isUpgrade
                          ? `Upgrade to ${plan.name}? You'll be charged a prorated difference today.`
                          : `Downgrade to ${plan.name}? Your plan changes immediately with no proration charge.`}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="flex-1"
                          size="sm"
                          onClick={() => setShowSwitchConfirm(null)}
                          disabled={!!switchingPlan}
                        >
                          Cancel
                        </Button>
                        <Button
                          className={`flex-1 ${isUpgrade ? 'gds-btn-primary' : 'gds-btn-secondary'}`}
                          size="sm"
                          onClick={() => handleSwitchPlan(plan.id)}
                          disabled={!!switchingPlan}
                        >
                          {switchingPlan === plan.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            `Confirm ${isUpgrade ? 'Upgrade' : 'Downgrade'}`
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      className={buttonClass}
                      onClick={() => !isActionDisabled && handlePlanAction(plan.id)}
                      disabled={isActionDisabled}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : isCurrentPlan ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Current Plan
                        </>
                      ) : isScheduledDowngradeTarget ? (
                        <>
                          <ArrowDown className="w-4 h-4 mr-2 text-orange-500" />
                          Downgrade Scheduled
                        </>
                      ) : isDowngradeLockedByPendingChange ? (
                        'Change Pending'
                      ) : isUpgrade ? (
                        <>
                          <ArrowUp className="w-4 h-4 mr-2" />
                          Upgrade to {plan.name}
                        </>
                      ) : isDowngrade ? (
                        <>
                          <ArrowDown className="w-4 h-4 mr-2" />
                          Downgrade to {plan.name}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Get Started
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  )}
                  {isScheduledDowngradeTarget && subscription?.pending_downgrade_effective_at && (
                    <p className="mt-2 text-xs text-orange-600 text-center">
                      Effective {formatDistanceToNow(new Date(subscription.pending_downgrade_effective_at), { addSuffix: true })}
                    </p>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      <div className="flex justify-end -mt-3">
        <Link
          to="/dashboard/billing/compare"
          className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors"
        >
          Compare all features {'->'}
        </Link>
      </div>

      {/* Invoice History */}
      {invoices.length > 0 && (
        <Card className="gds-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-500" />
                Invoice History
              </h3>
              <Button variant="ghost" size="sm" onClick={fetchInvoices} disabled={loadingInvoices}>
                {loadingInvoices ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-800">
                    <th className="text-left py-2 font-medium text-slate-500">Date</th>
                    <th className="text-left py-2 font-medium text-slate-500">Description</th>
                    <th className="text-left py-2 font-medium text-slate-500">Amount</th>
                    <th className="text-left py-2 font-medium text-slate-500">Status</th>
                    <th className="text-right py-2 font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="py-3 text-slate-600">
                        {format(new Date(inv.created_at), 'MMM d, yyyy')}
                      </td>
                      <td className="py-3 text-slate-600">
                        {inv.description || 'GDS Subscription'}
                      </td>
                      <td className="py-3 font-medium">
                        ${inv.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          inv.status === 'paid' 
                            ? 'text-emerald-600 bg-emerald-50' 
                            : inv.status === 'open'
                            ? 'text-amber-600 bg-amber-50'
                            : 'text-slate-600 bg-slate-100'
                        }`}>
                          {inv.status === 'paid' && <CheckCircle2 className="w-3 h-3" />}
                          {inv.status === 'open' && <AlertCircle className="w-3 h-3" />}
                          {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {inv.invoice_url && (
                            <a href={inv.invoice_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          {inv.invoice_pdf && (
                            <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-slate-700">
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract Info */}
      <Card className="gds-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-heading font-semibold mb-2">12-Month Contract</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                All plans require a 12-month commitment. We offer assured results (not guaranteed) 
                with full transparency and proof of work. 7-day grace period for payment issues before 
                execution is paused.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
